import Api from 'services/api'

import { downloadFile } from './documents'

const API_PARTICIPANTS = '/participants'
const API_PARTICIPANT_BY_ID = '/participants/:id'
const API_PARTICIPANT_DISTRIBUTIONS = '/participants/:id/distributions'
const API_PARTICIPANT_LOAN_DETAIL = '/participants/loanDetail/:loanNumber'
const API_PARTICIPANT_LOAN = '/participants/loan/:participantID'
const API_PARTICIPANT_STATUS = '/participants/:id/status'

export const filterParticipants = (filters: Record<string, any>) => {
  return Api.get(API_PARTICIPANTS, filters)
}

export const createParticipant = (data: Record<string, any>) => {
  return Api.post(API_PARTICIPANTS, data)
}

export const updateParticipant = (id: number, data: Record<string, any>) => {
  return Api.put(API_PARTICIPANT_BY_ID, data, { id })
}

// Portal access on/off. Pending cannot sign in; Active can. Converted investors arrive Pending.
export const updateParticipantStatus = (id: number, status: number) => {
  return Api.put(API_PARTICIPANT_STATUS, { status }, { id })
}

export const sendParticipantEmail = (id: number) => {
  return Api.post(`${API_PARTICIPANT_BY_ID}/email`, {}, { id })
}

export const updateParticipantPassword = (id: number, password: string) => {
  return Api.post(API_PARTICIPANT_BY_ID, { password }, { id })
}

export const getParticipantAssignedLoans = (id: number) => {
  return Api.get(API_PARTICIPANT_BY_ID, {}, { id })
}

// A participant's cross-loan participation (payment-distribution) history, one page at a time: a
// converted book runs to tens of thousands of rows for a single investor.
export const getParticipantDistributions = (id: number, pageNum = 0, count = 25) => {
  return Api.get(API_PARTICIPANT_DISTRIBUTIONS, { pageNum, count }, { id })
}

export const getLoanParticipantDetail = (loanNumber: number) => {
  return Api.get(API_PARTICIPANT_LOAN_DETAIL, {}, { loanNumber })
}

export const addParticipantLoan = (participantID: number, data: any) => {
  return Api.post(API_PARTICIPANT_LOAN, data, { participantID })
}

// ---- Investor portal -------------------------------------------------------
// The signed-in investor's own view: the loans they hold a slice of, the loans they were offered to
// buy, and the funds they have capital in. The investor is identified by their token, never by an
// id in the URL, so none of these take one.

// Its own top-level prefix, not `/investors/portal`: the staff `/participants/:id/...` routes would
// otherwise swallow it (see the controller). The backend still answers on `participantPortal` too.
const API_PARTICIPANT_PORTAL = '/investorPortal'
const API_PARTICIPANT_PORTAL_PROFILE = `${API_PARTICIPANT_PORTAL}/profile`
const API_PARTICIPANT_PORTAL_PORTFOLIO = `${API_PARTICIPANT_PORTAL}/portfolio`
const API_PARTICIPANT_PORTAL_DISTRIBUTIONS = `${API_PARTICIPANT_PORTAL}/distributions`
const API_PARTICIPANT_PORTAL_PAYOUTS = `${API_PARTICIPANT_PORTAL}/payouts`
const API_PARTICIPANT_PORTAL_DOCUMENTS = `${API_PARTICIPANT_PORTAL}/documents`
const API_PARTICIPANT_PORTAL_STATEMENT = `${API_PARTICIPANT_PORTAL}/statement`
const API_PARTICIPANT_PORTAL_LOAN = `${API_PARTICIPANT_PORTAL}/loans/:loanNumber`
const API_PARTICIPANT_PORTAL_LOAN_DOCUMENTS = `${API_PARTICIPANT_PORTAL}/loans/:loanNumber/documents`
const API_PARTICIPANT_PORTAL_DOCUMENT_DOWNLOAD = `${API_PARTICIPANT_PORTAL}/loans/:loanNumber/documents/download`
// Tax statements are the one set of documents here that is not loan-scoped: a 1099-INT is about an
// investor's year, not about one loan, so it has no loan number to hang off.
const API_PARTICIPANT_PORTAL_TAX_STATEMENTS = `${API_PARTICIPANT_PORTAL}/taxStatements`
const API_PARTICIPANT_PORTAL_TAX_STATEMENT_DOWNLOAD = `${API_PARTICIPANT_PORTAL}/taxStatements/:id/download`
const API_PARTICIPANT_PORTAL_TAX_CONSENT = `${API_PARTICIPANT_PORTAL}/taxStatements/consent`

export interface ParticipantPortalProfile {
  id: number
  no: number
  fullName: string
  email: string
  phone: string
  presentAddress: string
  requireReset: boolean
  payout: {
    payeeName: string
    bankName: string
    accountType: string
    /** Last four digits only - the portal never receives the full account number. */
    accountLast4: string
    achActive: boolean
  }
}

export interface ParticipantPortalLoanRow {
  loanNumber: number
  displayLoanNumber: string
  propertyAddress: string
  borrowerName: string
  servicingStatus: string
  servicingStatusLabel: string
  maturityDate: string
  participationDate: string
  invested: number
  principalReturned: number
  outstanding: number
  rate: number | null
  interestEarned: number
  feesEarned: number
  totalReceived: number
}

export interface ParticipantPortalDistribution {
  id: number
  loanNumber: number
  displayLoanNumber?: string
  propertyAddress?: string
  borrowerName?: string
  date: string
  principal: number
  interest: number
  fees: number
  servicingFee: number
  amount: number
  ownershipPct: number | null
  reversal: boolean
  paid: boolean
  paidDate: string
  paymentMethod: string
  referenceNumber: string
}

export interface ParticipantPortalDocument {
  loanNumber: number
  displayLoanNumber: string
  propertyAddress: string
  borrowerName: string
  key: string
  name: string
  category: string
  sharedAt: number | null
}

export interface ParticipantPortalTaxStatement {
  id: number
  taxYear: number
  formType: string
  formTitle: string
  fileName: string
  postedAt: string | null
  /** When it stops being available online. After this the investor has to ask for a copy. */
  accessibleUntil: string | null
  loanNumber: number | null
}

export interface ParticipantPortalTaxConsent {
  consented: boolean
  email: string
  consentedAt: string | null
  withdrawnAt: string | null
}

export const getParticipantPortalProfile = (): Promise<ParticipantPortalProfile> => {
  return Api.get(API_PARTICIPANT_PORTAL_PROFILE)
}

export const updateParticipantPortalProfile = (data: {
  fullName?: string
  phone?: string
  presentAddress?: string
}): Promise<ParticipantPortalProfile> => {
  return Api.put(API_PARTICIPANT_PORTAL_PROFILE, data)
}

/** What the portfolio leads with, whether the investor holds loans, funds or both. */
export interface InvestorPortalCombined {
  hasLoans: boolean
  hasFunds: boolean
  loanValue: number
  fundValue: number
  totalValue: number
  loanIncome: number
  fundIncome: number
  totalIncome: number
  totalContributed: number
  fundCount: number
  fundContributed: number
}

export const getParticipantPortalPortfolio = (): Promise<{
  summary: Record<string, number | null>
  loans: ParticipantPortalLoanRow[]
  funds: InvestorPortalFundPosition[]
  combined: InvestorPortalCombined
}> => {
  return Api.get(API_PARTICIPANT_PORTAL_PORTFOLIO)
}

export const getParticipantPortalLoan = (loanNumber: number | string): Promise<Record<string, any>> => {
  return Api.get(API_PARTICIPANT_PORTAL_LOAN, {}, { loanNumber })
}

export const getParticipantPortalDistributions = (
  filters: { from?: string; to?: string; loanNumber?: number | string } = {},
): Promise<{ distributions: ParticipantPortalDistribution[]; totals: Record<string, number> }> => {
  return Api.get(API_PARTICIPANT_PORTAL_DISTRIBUTIONS, filters)
}

export const getParticipantPortalPayouts = (): Promise<{ payouts: Record<string, any>[] }> => {
  return Api.get(API_PARTICIPANT_PORTAL_PAYOUTS)
}

export const getParticipantPortalDocuments = (): Promise<{ documents: ParticipantPortalDocument[] }> => {
  return Api.get(API_PARTICIPANT_PORTAL_DOCUMENTS)
}

export const getParticipantPortalLoanDocuments = (
  loanNumber: number | string,
): Promise<{ documents: ParticipantPortalDocument[] }> => {
  return Api.get(API_PARTICIPANT_PORTAL_LOAN_DOCUMENTS, {}, { loanNumber })
}

export const getParticipantPortalTaxStatements = (): Promise<{
  statements: ParticipantPortalTaxStatement[]
}> => {
  return Api.get(API_PARTICIPANT_PORTAL_TAX_STATEMENTS)
}

/** A signed URL for one of the investor's own statements. Re-checked server side, not by id alone. */
export const downloadParticipantPortalTaxStatement = (
  id: number,
): Promise<{ url?: string; content?: string }> => {
  return Api.get(API_PARTICIPANT_PORTAL_TAX_STATEMENT_DOWNLOAD, {}, { id })
}

export const getParticipantPortalTaxConsent = (): Promise<ParticipantPortalTaxConsent> => {
  return Api.get(API_PARTICIPANT_PORTAL_TAX_CONSENT)
}

export const setParticipantPortalTaxConsent = (data: {
  consented: boolean
  email?: string
}): Promise<any> => {
  return Api.post(API_PARTICIPANT_PORTAL_TAX_CONSENT, data)
}

export const downloadParticipantPortalDocument = (
  loanNumber: number | string,
  key: string,
): Promise<{ url?: string; content?: string }> => {
  return Api.post(API_PARTICIPANT_PORTAL_DOCUMENT_DOWNLOAD, { key }, { loanNumber })
}

/** The investor's own statement for a period, streamed straight to a file. */
export const downloadParticipantPortalStatement = async (period: {
  periodStart?: string
  periodEnd?: string
  asOf?: string
}) => {
  const res: Blob = await Api.get(
    API_PARTICIPANT_PORTAL_STATEMENT,
    period,
    {},
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile(`Investor Statement.pdf`, res)
}

/** Which parts of the portal this investor has: loans, loan purchases, funds. */
export const getInvestorPortalSections = (): Promise<{
  origination: boolean
  servicing: boolean
  fund: boolean
}> => {
  return Api.get(`${API_PARTICIPANT_PORTAL}/sections`)
}

// ---- Investor portal: mortgage funds ---------------------------------------
// What this investor holds in the company's funds. Not loans, so none of it goes through the
// loan-ownership gate; the fund side is resolved from the token's investor.

export interface InvestorPortalFundPosition {
  id: number
  fundId: number
  fundName: string
  fundNumber: number
  status: string
  commitment: number
  contributed: number
  withdrawn: number
  distributionsPaid: number
  capitalBalance: number
  shares: number
  value: number
}

export const getInvestorPortalFunds = (): Promise<{ positions: InvestorPortalFundPosition[] }> => {
  return Api.get(`${API_PARTICIPANT_PORTAL}/funds`)
}

export const getInvestorPortalFundActivity = (
  filters: { from?: string; to?: string } = {},
): Promise<{ transactions: Record<string, any>[] }> => {
  return Api.get(`${API_PARTICIPANT_PORTAL}/funds/activity`, filters)
}

/** The investor's own fund statement, across every fund or scoped to one of them. */
export const downloadInvestorPortalFundStatement = async (
  filters: { from?: string; to?: string; fundId?: number } = {},
) => {
  const res: Blob = await Api.get(
    `${API_PARTICIPANT_PORTAL}/funds/statement`,
    filters,
    {},
    { responseType: 'blob', timeout: 300000 },
  )
  downloadFile(`Fund Statement.pdf`, res)
}

// ---- Investor portal: loans offered for purchase ----------------------------
// The origination side. The list itself is the ordinary pipeline query, which resolves this
// investor from their token; these are the per-loan pieces around it.

export const getInvestorPortalPurchaseStatus = (loanNumber: number | string) => {
  return Api.get(`${API_PARTICIPANT_PORTAL}/purchases/:loanNumber/status`, {}, { loanNumber })
}

export const setInvestorPortalPurchaseStatus = (loanNumber: number | string, investorStatus: string) => {
  return Api.post(`${API_PARTICIPANT_PORTAL}/purchases/:loanNumber/status`, { investorStatus }, { loanNumber })
}

export const getInvestorPortalPurchaseChat = (loanNumber: number | string) => {
  return Api.get(`${API_PARTICIPANT_PORTAL}/purchases/:loanNumber/chat`, {}, { loanNumber })
}

export const addInvestorPortalPurchaseChat = (loanNumber: number | string, data: Record<string, any>) => {
  return Api.post(`${API_PARTICIPANT_PORTAL}/purchases/:loanNumber/chat`, data, { loanNumber })
}

export const getInvestorPortalPurchaseOverview = (loanNumber: number | string) => {
  return Api.get(`${API_PARTICIPANT_PORTAL}/purchases/:loanNumber/overview`, {}, { loanNumber })
}
